/**
 * Silently posts the driver's GPS coordinates to the backend every 30s
 * while the driver is on-call (`available=true`). No UI — mount once
 * at the top of the driver PWA shell.
 *
 * Handling policies:
 *  - No GPS permission → we don't ask (the browser's own prompt handles
 *    that when watchPosition starts). If denied, we degrade silently.
 *  - Stale readings (accuracy > 500m) are still posted; the dispatch
 *    engine deprioritises stale-GPS anyway (business rule).
 *  - `available=false` → we stop pinging to save battery.
 */
import { useEffect, useRef } from "react";
import driverApi from "@/lib/driverApi";

const PING_INTERVAL_MS = 30_000;

export default function LocationTracker({ enabled }) {
  const lastSent = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof navigator === "undefined" || !navigator.geolocation) return undefined;

    let watchId = null;
    let mounted = true;

    const send = async (pos) => {
      const now = Date.now();
      // Rate-limit — never faster than PING_INTERVAL_MS
      if (now - lastSent.current < PING_INTERVAL_MS - 1000) return;
      lastSent.current = now;
      try {
        await driverApi.post("/driver/location", {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      } catch {
        /* silent — dispatch has stale-GPS fallback */
      }
    };

    // Kick off a single reading fast, then keep watching for coarse changes.
    navigator.geolocation.getCurrentPosition(
      (pos) => { if (mounted) send(pos); },
      () => {},
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 20_000 },
    );
    watchId = navigator.geolocation.watchPosition(
      (pos) => { if (mounted) send(pos); },
      () => {},
      { enableHighAccuracy: false, maximumAge: 30_000, timeout: 60_000 },
    );

    // Fallback poll if watchPosition throttles heavily (some Android WebViews)
    const iv = setInterval(() => {
      if (!mounted) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => send(pos), () => {},
        { enableHighAccuracy: false, maximumAge: 30_000, timeout: 15_000 },
      );
    }, PING_INTERVAL_MS);

    return () => {
      mounted = false;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      clearInterval(iv);
    };
  }, [enabled]);

  return null;
}
